import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapDef } from "@neon-purge/shared";
import { toonGradient } from "./mapTextures.js";
import { scaleTexture } from "./procTextures.js";
import { bake, blob, richCreatures } from "./creature.js";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KRAKEN_URL, SHARK_URL, buildSharkModel, disposeSkinned, preloadSeaModel, seaModelIfReady, seaModelsEnabled, updateSharkModel, type SharkRig } from "./seaModels.js";
import { holdLoadScreen, loadScreenOpen } from "./loadGate.js";
import { CROW_URL, FISH_URL, HARE_URL, MorphHerd, ambientModelIfReady, preloadAmbientModel } from "./ambientModels.js";
import { realModelsEnabled } from "./gltfAssets.js";
import { dragonModelArmed, mountDragonModel, type DragonModel } from "./dragonModel.js";

/**
 * Purely-decorative wildlife to bring maps to life — a few birds circling
 * overhead and small critters wandering the ground. Client-only, no netcode,
 * no gameplay effect. Cheap low-poly toon meshes.
 */
// Birds are INSTANCED like fish (iteration after 396): `g`/`body`/`wingL`/`wingR`
// are NON-rendered Object3D proxies carrying the exact same local transforms the
// old per-bird meshes had, so updateBirds moves them byte-identically; the visible
// geometry is three shared InstancedMeshes (`birdBodyIM` + `birdWingLIM` +
// `birdWingRIM`), and `slot` is this bird's instance index. Birds are on EVERY map.
interface Bird { g: THREE.Object3D; body: THREE.Object3D; wingL: THREE.Object3D; wingR: THREE.Object3D; slot: number; cx: number; cz: number; r: number; a: number; speed: number; y: number; dead?: number; vy?: number; ph?: number; gw?: number; }
/** `model`: the realistic dragon (dragonModel.ts) mounted in `g` once the
 *  module is armed under the load screen; the procedural parts are then hidden
 *  and `tint` (the palette body colour) carries over to its hide. */
interface Dragon { g: THREE.Group; wingL: THREE.Group; wingR: THREE.Group; cx: number; cz: number; r: number; a: number; speed: number; y: number; ph: number; tint: string; model: DragonModel | null; }
// Critters are INSTANCED like the birds/fish (hwaccel 2026-08-25): `g` is a
// NON-rendered Object3D proxy carrying the exact same whole-body transform the
// old per-critter Group had, so updateCritters moves it byte-identically; the
// visible geometry is two shared InstancedMeshes (`critterBodyIM` +
// `critterDarkIM`), and `slot` is this critter's instance index. Critters are
// on EVERY map (4-7). Per-critter body tint (brown/grey) rides instanceColor.
interface Critter { g: THREE.Object3D; slot: number; x: number; z: number; gy: number; dir: number; speed: number; t: number; turnAt: number; minX: number; maxX: number; minZ: number; maxZ: number; dead?: number; ph?: number; }
interface GroundPatch { minX: number; maxX: number; minZ: number; maxZ: number; top: number; }
/** A sea creature swimming inside a water volume (fish / shark).
 *  Sharks are a real rendered Group. Fish are INSTANCED (iteration after 373):
 *  `g`/`bodyProxy`/`tail` are NON-rendered Object3D proxies carrying the exact
 *  same local transforms the old per-fish meshes had, so updateSwimmers moves
 *  them byte-identically; the visible geometry is two shared InstancedMeshes
 *  (`fishBodyIM` + `fishTailIM`), and `slot` is this fish's instance index. */
interface Swimmer { g: THREE.Object3D; tail: THREE.Object3D; bodyProxy?: THREE.Object3D; slot?: number; x: number; y: number; z: number; dir: number; pitch: number; speed: number; t: number; turnAt: number; big: boolean; shark?: SharkRig; ph?: number; }

// Scratch — hoisted so the per-frame fish instance write allocates nothing.
const FISH_ZERO_MAT = new THREE.Matrix4().makeScale(0, 0, 0); // collapses an unused slot to a point (invisible)
const FISH_TMP_COL = new THREE.Color();
// Scratch for the realistic herds (ambientModels.ts): the bird's model pose is
// composed here (its own heading + bank), never allocated per frame.
const HERD_TMP = new THREE.Object3D();
/** Realistic-herd sizes (metres nose→tail) — matched to the procedural
 *  animals they replace (bird ~0.6 m body / ~1.1 m span; fish ~0.8 m incl.
 *  tail; hare ~0.75 m), times each animal's own random group scale. */
const CROW_LEN = 0.65, FISH_LEN = 0.8, HARE_LEN = 0.72;
/** Wingbeat period (s) of the realistic bird; the procedural flap was ~0.56 s. */
const CROW_BEAT = 0.52;
/** Tail-beat period (s) of the realistic fish (procedural swish ~0.52 s). */
const FISH_BEAT = 0.62;
/** Ground covered per hop cycle (m) — the hare's cycle rate follows its speed. */
const HARE_STRIDE = 0.75;
// per-instance tints over the painted albedo: a little natural variation
const CROW_TINTS = ["#ffffff", "#e6e8ee", "#d4d8e0", "#f2ece4"];
const FISH_TINTS = ["#ffffff", "#e8f2f0", "#f4ece0", "#dfe8f4", "#f0f0f0"];
const HARE_TINTS = ["#ffffff", "#efe6da", "#e2e2e2", "#f6efe2"];

/** European dragon rig: both wing groups pivot at the shoulders. The LEFT
 *  wing is the right one mirrored via parent scale.x = -1, so setting the
 *  SAME rotation.z on both flaps them symmetrically. */
export interface DragonRig { group: THREE.Group; wingL: THREE.Group; wingR: THREE.Group; }

/**
 * A realistic (still low-poly toon) european dragon in soaring pose, forward
 * = -z, ~7 units long before scaling. Segmented S-neck and tail built from
 * tapered oriented cylinders with ball joints, dorsal spike ridge, proper
 * skull (brow, twin swept horns, parted jaws with teeth, glowing eyes),
 * belly scutes, tucked clawed legs, and scallop-edged bat wings with real
 * arm/finger bones over the membrane.
 */
export function buildEuropeanDragon(
  grad: THREE.Texture | null,
  palette: { body: string; membrane: string; belly: string },
): DragonRig {
  const scales = scaleTexture();
  const body = new THREE.MeshToonMaterial({ color: palette.body, gradientMap: grad, map: scales });
  const memb = new THREE.MeshToonMaterial({ color: palette.membrane, gradientMap: grad, side: THREE.DoubleSide, map: scales });
  const belly = new THREE.MeshToonMaterial({ color: palette.belly, gradientMap: grad, map: scales });
  const bone = new THREE.MeshToonMaterial({ color: "#d8cfae", gradientMap: grad });
  const eyeM = new THREE.MeshBasicMaterial({ color: "#ffcf4a" });
  const g = new THREE.Group();
  // hwaccel: the dragon BODY (torso/neck/head/spine/tail/legs + every bone
  // spike/horn/claw) is RIGID — only the wings animate (rotation.z per frame).
  // So body parts are collected here rather than added to `g` one draw call
  // each, then baked + merged by material below (~82 meshes -> 5). Wing parts
  // (parent !== g) still add normally so they can flap. See the merge at end.
  const bodyParts: THREE.Mesh[] = [];

  /** Tapered strut from a to b (radius r1 at a, r2 at b). */
  const seg = (
    ax: number, ay: number, az: number, bx: number, by: number, bz: number,
    r1: number, r2: number, mat: THREE.Material, parent: THREE.Object3D = g,
  ): THREE.Mesh => {
    const a = new THREE.Vector3(ax, ay, az);
    const dir = new THREE.Vector3(bx - ax, by - ay, bz - az);
    const len = dir.length();
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r2, r1, len, 8), mat);
    m.position.copy(a).addScaledVector(dir, 0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
    m.castShadow = true;
    if (parent === g) bodyParts.push(m); else parent.add(m);
    return m;
  };
  /** Sphere/ellipsoid at a point. */
  const ball = (
    x: number, y: number, z: number, r: number, mat: THREE.Material,
    sx = 1, sy = 1, sz = 1, parent: THREE.Object3D = g,
  ): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8), mat);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.castShadow = true;
    if (parent === g) bodyParts.push(m); else parent.add(m);
    return m;
  };
  /** Back-swept dorsal spike. */
  const spike = (x: number, y: number, z: number, len: number, r: number, tilt = 0.45): void => {
    const m = new THREE.Mesh(new THREE.ConeGeometry(r, len, 6), bone);
    m.position.set(x, y + len * 0.35, z);
    m.rotation.x = tilt; // leans back toward the tail
    m.castShadow = true;
    bodyParts.push(m);
  };

  // ---- torso: chest → barrel → hips, shoulder bulges, lighter belly scutes.
  // The chest carries a deep KEEL (flight muscle anchor — every big flier has
  // one) and the haunches read heavier than the shoulders, like a real
  // quadruped that launches from its hind legs.
  ball(0, 0.05, -0.9, 1, body, 1.3, 1.2, 1.9);
  ball(0, -0.35, -0.75, 1, body, 0.72, 0.62, 1.25); // chest keel
  ball(0, 0, 0.2, 1, body, 1.14, 1.0, 2.2);
  ball(0, -0.02, 1.35, 1, body, 1.05, 0.95, 1.6); // heavier haunches
  for (const sx of [-1, 1]) ball(sx * 0.55, 0.32, -0.85, 1, body, 0.5, 0.45, 0.7);
  for (let i = 0; i < 5; i++) ball(0, -0.5 + i * 0.015, -1.2 + i * 0.62, 1, belly, 0.62 - i * 0.04, 0.28, 0.42);

  // ---- neck: S-curve of tapered segments with ball joints + spike ridge.
  // 5 points, not 4 — the extra sample smooths the S so the joints stop
  // reading as a chain of beads.
  const neckPts: [number, number, number][] = [
    [0, 0.3, -1.7], [0, 0.62, -2.2], [0, 1.0, -2.66], [0, 1.42, -3.02], [0, 1.8, -3.25],
  ];
  const neckR = [0.46, 0.4, 0.34, 0.29, 0.26];
  for (let i = 0; i < neckPts.length - 1; i++) {
    const [ax, ay, az] = neckPts[i], [bx, by, bz] = neckPts[i + 1];
    seg(ax, ay, az, bx, by, bz, neckR[i], neckR[i + 1], body);
    ball(bx, by, bz, neckR[i + 1] * 0.96, body);
    spike(ax, ay + neckR[i] * 0.85, az, 0.28 - i * 0.035, 0.075, 0.55);
  }
  // throat: a soft pouch under the jaw hinge — the underside of a neck is
  // never the same tube as the top
  ball(0, 1.52, -3.1, 1, belly, 0.24, 0.3, 0.4);

  // ---- head: wedge skull with a LONG snout (the old ball head read as a
  // knob), surfaced amber eyes under brow ridges, parted jaws with fangs,
  // twin swept horns, cheek spikes and a small crest
  ball(0, 1.96, -3.55, 1, body, 0.5, 0.42, 0.78);
  spike(0, 2.22, -3.35, 0.22, 0.07, 0.8); // crest
  seg(0, 2.0, -3.9, 0, 1.86, -4.8, 0.25, 0.12, body); // long upper snout
  ball(0, 1.86, -4.8, 0.12, body);
  seg(0, 1.76, -3.85, 0, 1.64, -4.6, 0.15, 0.07, body); // lower jaw, mouth parted
  ball(0, 1.64, -4.6, 0.07, body);
  for (const sx of [-1, 1]) {
    ball(sx * 0.26, 2.14, -3.72, 1, body, 0.18, 0.09, 0.3); // brow ridge
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), eyeM);
    eye.position.set(sx * 0.42, 2.02, -3.7); // ON the skull surface — visible
    bodyParts.push(eye);
    for (const tz of [-4.15, -4.45]) { // fangs from the upper jaw
      const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.12, 5), bone);
      tooth.position.set(sx * 0.09, 1.78, tz);
      tooth.rotation.x = Math.PI;
      bodyParts.push(tooth);
    }
    for (const tz of [-4.0, -4.3]) { // opposing teeth from the lower jaw —
      // a parted mouth with only an upper row read as a rubber glove
      const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.09, 5), bone);
      tooth.position.set(sx * 0.07, 1.7, tz);
      bodyParts.push(tooth);
    }
    // three-segment horns sweeping back over the neck with a slight outward
    // curl — the extra knuckle turns a spike into a HORN
    seg(sx * 0.18, 2.16, -3.45, sx * 0.32, 2.48, -3.1, 0.09, 0.06, bone);
    seg(sx * 0.32, 2.48, -3.1, sx * 0.44, 2.62, -2.62, 0.06, 0.035, bone);
    seg(sx * 0.44, 2.62, -2.62, sx * 0.52, 2.6, -2.28, 0.035, 0.01, bone);
    // ear frill: a swept membrane fan behind the jaw
    const frill = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.42, 4), memb);
    frill.position.set(sx * 0.34, 2.0, -3.18);
    frill.rotation.set(1.1, 0, sx * -0.6);
    frill.scale.z = 0.25;
    frill.castShadow = true;
    bodyParts.push(frill);
    // cheek spikes
    seg(sx * 0.28, 1.88, -3.5, sx * 0.58, 1.78, -3.2, 0.05, 0.01, bone);
    // nostrils near the nose tip
    const nos = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 5), memb);
    nos.position.set(sx * 0.07, 1.94, -4.72);
    bodyParts.push(nos);
  }

  // ---- spine ridge down the back
  const ridge: [number, number, number, number][] = [
    [0, 0.98, -1.35, 0.4], [0, 1.06, -0.65, 0.44], [0, 1.02, 0.05, 0.44],
    [0, 0.9, 0.75, 0.4], [0, 0.72, 1.5, 0.34], [0, 0.52, 2.3, 0.28],
  ];
  for (const [x, y, z, len] of ridge) spike(x, y, z, len, 0.11);

  // ---- tail: long tapering chain that DROOPS off the hips under its own
  // weight before sweeping up to the spade (the old straight ramp read as a
  // pole). 7 samples for a real curve, ridge spikes fading out along it.
  const tailPts: [number, number, number][] = [
    [0, 0, 2.0], [0, -0.08, 2.9], [0, -0.12, 3.8], [0, -0.02, 4.7],
    [0, 0.16, 5.5], [0, 0.34, 6.2], [0, 0.48, 6.8],
  ];
  const tailR = [0.55, 0.42, 0.31, 0.22, 0.15, 0.09, 0.05];
  for (let i = 0; i < tailPts.length - 1; i++) {
    const [ax, ay, az] = tailPts[i], [bx, by, bz] = tailPts[i + 1];
    seg(ax, ay, az, bx, by, bz, tailR[i], tailR[i + 1], body);
    ball(bx, by, bz, tailR[i + 1] * 0.96, body);
    if (i < 5) spike(bx, by + tailR[i + 1] * 0.8, bz, 0.22 - i * 0.033, 0.065, 0.5);
  }
  { // spade: flattened arrowhead
    const spade = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.8, 4), memb);
    spade.position.set(0, 0.52, 7.15);
    spade.rotation.x = Math.PI / 2; // point backward
    spade.scale.y = 1; spade.scale.z = 0.3;
    spade.castShadow = true;
    bodyParts.push(spade);
  }

  // ---- tucked legs with claws (flight pose)
  for (const sx of [-1, 1]) {
    // hind: thigh → folded shin → foot with claws trailing
    seg(sx * 0.55, -0.2, 1.05, sx * 0.75, -0.72, 0.7, 0.3, 0.19, body);
    ball(sx * 0.75, -0.72, 0.7, 0.19, body);
    seg(sx * 0.75, -0.72, 0.7, sx * 0.68, -0.85, 1.5, 0.16, 0.1, body);
    ball(sx * 0.68, -0.85, 1.5, 0.11, body);
    for (const cx of [-0.08, 0, 0.08]) {
      seg(sx * (0.68 + cx), -0.85, 1.5, sx * (0.68 + cx * 2.2), -0.95, 1.78, 0.04, 0.008, bone);
    }
    // fore: small tucked arm under the chest
    seg(sx * 0.5, -0.3, -1.15, sx * 0.62, -0.75, -0.85, 0.2, 0.13, body);
    ball(sx * 0.62, -0.75, -0.85, 0.13, body);
    seg(sx * 0.62, -0.75, -0.85, sx * 0.56, -0.85, -1.35, 0.11, 0.07, body);
    for (const cx of [-0.06, 0.06]) {
      seg(sx * (0.56 + cx), -0.85, -1.35, sx * (0.56 + cx * 2), -0.92, -1.58, 0.03, 0.007, bone);
    }
  }

  // ---- wings: TWO panels per wing (arm panel + finger panel drooped at the
  // wrist) so the wing has a real bend and reads from every angle instead of
  // collapsing to a flat skewer edge-on. Scalloped trailing edges, bones on
  // top. Built for the RIGHT side; the left is the same group mirrored.
  const innerShape = new THREE.Shape();
  innerShape.moveTo(0, 0.5);             // shoulder, leading edge
  innerShape.lineTo(1.0, 0.75);          // elbow
  innerShape.lineTo(2.2, 0.95);          // wrist
  innerShape.lineTo(2.2, -1.42);         // wrist chord (panel seam)
  innerShape.lineTo(1.65, -1.6);         // finger-4 tip
  innerShape.quadraticCurveTo(0.8, -1.55, 0, -1.65); // scallop to the hip
  innerShape.closePath();
  const outerShape = new THREE.Shape();  // wrist-local coords
  outerShape.moveTo(0, 0);               // wrist leading edge
  outerShape.lineTo(2.4, -0.45);         // wingtip (finger 1)
  outerShape.quadraticCurveTo(1.8, -0.85, 1.65, -1.5);  // scallop to finger 2
  outerShape.quadraticCurveTo(0.95, -1.75, 0.65, -2.15); // scallop to finger 3
  outerShape.lineTo(0, -2.37);           // back to the seam
  outerShape.closePath();
  const buildWing = (): THREE.Group => {
    const w = new THREE.Group();
    const inner = new THREE.Mesh(new THREE.ShapeGeometry(innerShape, 6), memb);
    inner.rotation.x = -Math.PI / 2; // lay flat: shape +y → -z (forward)
    inner.castShadow = true;
    w.add(inner);
    // arm bones on the inner panel (shape (sx, sy) → (sx, 0, -sy))
    seg(0, 0.03, -0.45, 1.0, 0.03, -0.7, 0.13, 0.1, body, w);   // humerus
    ball(1.0, 0.05, -0.7, 0.1, body, 1, 1, 1, w);               // elbow
    seg(1.0, 0.03, -0.7, 2.2, 0.03, -0.92, 0.09, 0.07, body, w); // forearm
    ball(2.2, 0.05, -0.92, 0.09, body, 1, 1, 1, w);              // wrist knuckle
    seg(2.2, 0.025, -0.95, 1.65, 0.025, 1.6, 0.04, 0.008, body, w); // finger 4 (inner panel)
    // OUTER panel: fingers + membrane in a wrist group, drooped downward
    const wrist = new THREE.Group();
    wrist.position.set(2.2, 0.03, -0.95);
    wrist.rotation.z = -0.35; // the characteristic wing-knuckle bend
    const outer = new THREE.Mesh(new THREE.ShapeGeometry(outerShape, 6), memb);
    outer.rotation.x = -Math.PI / 2;
    outer.position.z = -0.95 + 0.95; // shape built wrist-local already
    outer.castShadow = true;
    wrist.add(outer);
    for (const [fx, fy] of [[2.4, -0.45], [1.65, -1.5], [0.65, -2.15]] as const) {
      seg(0, 0.025, 0, fx, 0.025, -fy, 0.045, 0.008, body, wrist);
    }
    seg(0, 0.03, 0, 0.3, 0.1, -0.4, 0.045, 0.008, bone, wrist); // thumb claw
    w.add(wrist);
    // hwaccel: the wing is RIGID relative to its own group — the wrist bend is a
    // STATIC transform, only the whole wing's rotation.z/y animates per frame.
    // So bake each part's matrix (relative to w, which is at identity here) into
    // its geometry and merge by material: 3 draw calls per wing (memb/body/bone)
    // in place of ~11. w stays the animated pivot; wingL's scale.x=-1 mirror and
    // the per-frame flap apply to w after the merge exactly as before. Proven
    // vertex-for-vertex under a live flap+group transform by bench-dragon-wing-merge.
    w.updateMatrixWorld(true);
    const wingParts: THREE.Mesh[] = [];
    w.traverse((o) => { if ((o as THREE.Mesh).isMesh) wingParts.push(o as THREE.Mesh); });
    const wingByMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
    for (const m of wingParts) {
      const geo = (m.geometry as THREE.BufferGeometry).applyMatrix4(m.matrixWorld);
      const mat = m.material as THREE.Material;
      let bucket = wingByMat.get(mat);
      if (!bucket) { bucket = []; wingByMat.set(mat, bucket); }
      bucket.push(geo);
    }
    w.clear();
    for (const [mat, geos] of wingByMat) {
      const merged = mergeGeometries(geos, false);
      if (merged) {
        for (const gg of geos) gg.dispose();
        const mm = new THREE.Mesh(merged, mat);
        mm.castShadow = true;
        w.add(mm);
      } else {
        for (const gg of geos) { const mm = new THREE.Mesh(gg, mat); mm.castShadow = true; w.add(mm); }
      }
    }
    return w;
  };
  const wingR = buildWing();
  wingR.position.set(0.55, 0.45, -0.75);
  g.add(wingR);
  const wingL = buildWing();
  wingL.position.set(-0.55, 0.45, -0.75);
  wingL.scale.x = -1; // mirror — same rotation.z now flaps both symmetrically
  g.add(wingL);

  // MERGE the rigid body: bake each part's local matrix into its geometry and
  // combine by material -> one draw call per material (5) in place of the ~82
  // individual body meshes. mergeGeometries preserves every triangle exactly
  // (proven by agent/bench-dragon-merge.mjs), so the silhouette — and the
  // shadow pass, which now casts 5 meshes not 82 — is byte-identical. The wings
  // are untouched groups so they still flap.
  const bodyByMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
  for (const m of bodyParts) {
    m.updateMatrix();
    const geo = (m.geometry as THREE.BufferGeometry).applyMatrix4(m.matrix);
    const mat = m.material as THREE.Material;
    let bucket = bodyByMat.get(mat);
    if (!bucket) { bucket = []; bodyByMat.set(mat, bucket); }
    bucket.push(geo);
  }
  for (const [mat, geos] of bodyByMat) {
    const merged = mergeGeometries(geos, false);
    if (merged) {
      for (const gg of geos) gg.dispose();
      const mm = new THREE.Mesh(merged, mat);
      mm.castShadow = true;
      g.add(mm);
    } else {
      // All parts share indexed pos/normal/uv, so this never triggers — but
      // never drop the dragon: fall back to individually-baked meshes.
      for (const gg of geos) { const mm = new THREE.Mesh(gg, mat); mm.castShadow = true; g.add(mm); }
    }
  }

  return { group: g, wingL, wingR };
}

export class AmbientLife {
  private birds: Bird[] = [];
  private critters: Critter[] = [];
  private grp = new THREE.Group();
  /** Killed animals come back somewhere ELSE on the map after a short delay. */
  private respawnQueue: { kind: "bird" | "critter" | "fish" | "shark"; at: number }[] = [];
  // map envelope captured for respawning (constructor computes once)
  private env!: { minX: number; maxX: number; minZ: number; maxZ: number; spanX: number; spanZ: number; cx: number; cz: number; big: number; maxY: number; grad: THREE.Texture };
  /** Flying giblets from the kill easter egg — cheap boxes with gravity. */
  private gore: { m: THREE.Mesh; vx: number; vy: number; vz: number; sx: number; sz: number; born: number }[] = [];
  /** Walkable box tops — critters snap to these and turn away from voids. */
  private grounds: GroundPatch[] = [];

  /** Highest walkable surface under (x,z), or null over a void. */
  private groundAt(x: number, z: number): number | null {
    let top: number | null = null;
    for (const p of this.grounds) {
      if (x < p.minX || x > p.maxX || z < p.minZ || z > p.maxZ) continue;
      if (top === null || p.top > top) top = p.top;
    }
    return top;
  }

  constructor(private scene: THREE.Scene, map: MapDef) {
    const grad = toonGradient();
    const { min, max } = map.bounds;
    const spanX = max[0] - min[0];
    const spanZ = max[2] - min[2];
    const cx = (min[0] + max[0]) / 2;
    const cz = (min[2] + max[2]) / 2;
    const big = Math.max(spanX, spanZ);

    // rand helpers (Math.random is fine — purely cosmetic, never simulated)
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);

    // walkable surfaces: reasonably flat box tops at ground-ish height (ground
    // slabs, fort platforms, flying islands, ship decks). Critters stay on
    // these. NB exclude water volumes + the deep seabed — cats/rabbits must
    // NOT wander across the sea surface or the ocean floor.
    this.grounds = map.boxes
      .filter((b) => b.tag !== "water" && b.p[1] + b.s[1] / 2 >= -2 && b.s[0] >= 3 && b.s[2] >= 3 && b.p[1] + b.s[1] / 2 <= 16)
      .map((b) => ({
        minX: b.p[0] - b.s[0] / 2, maxX: b.p[0] + b.s[0] / 2,
        minZ: b.p[2] - b.s[2] / 2, maxZ: b.p[2] + b.s[2] / 2,
        top: b.p[1] + b.s[1] / 2,
      }));

    this.env = { minX: min[0], maxX: max[0], minZ: min[2], maxZ: max[2], spanX, spanZ, cx, cz, big, maxY: max[1], grad };

    // --- birds circling overhead ---
    const nBirds = big > 90 ? 8 : 5;
    this.buildBirdPool(nBirds);
    for (let i = 0; i < nBirds; i++) this.spawnBird(i);

    // --- ground critters wandering the terrain ---
    const nCritters = big > 90 ? 7 : 4;
    this.buildCritterPool(nCritters);
    for (let i = 0; i < nCritters; i++) this.spawnCritter();

    // --- HUGE european dragons circling the castle sky (medieval only) ---
    if (map.id === "medieval_castle") for (let i = 0; i < 3; i++) this.spawnDragon(i);

    // --- SEA CREATURES swimming in the water volume (pirate ship etc.) ---
    // A FLOOD BASIN rests buried under the map floor (canyon's flash flood,
    // resting surface below ground) — its shoal would swim underground forever,
    // so only a standing sea whose surface is visible at rest gets fish/sharks.
    const waters = map.boxes.filter((b) => b.tag === "water" && b.p[1] + b.s[1] / 2 > -0.4);
    if (waters.length) {
      const w = waters[0];
      // keep the shoal near the play space (around centre) so players see it
      const rx = Math.min(60, w.s[0] / 2 - 4), rz = Math.min(60, w.s[2] / 2 - 4);
      this.water = {
        minX: w.p[0] - rx, maxX: w.p[0] + rx, minZ: w.p[2] - rz, maxZ: w.p[2] + rz,
        top: w.p[1] + w.s[1] / 2 - 0.8, bottom: w.p[1] - w.s[1] / 2 + 0.8,
      };
      this.buildFishPool(22);
      for (let i = 0; i < 22; i++) this.spawnFish();
      // REALISTIC SHARKS (seaModels.ts): use the glTF if it is already parsed;
      // otherwise start the fetch, build the procedural sharks now, and swap
      // them for the real model if it lands while the load screen is still up
      // (renderer.precompile waits on the gate before compiling, so the swap
      // is in the scene for the shader compile). Late / failed / low-spec:
      // the procedural sharks stay for the round.
      this.sharkModel = seaModelIfReady(SHARK_URL);
      for (let i = 0; i < 3; i++) this.spawnShark();
      if (!this.sharkModel && seaModelsEnabled()) {
        const ticket = holdLoadScreen(preloadSeaModel(SHARK_URL).then((g) => {
          if (g && !this.disposed && loadScreenOpen(ticket)) this.upgradeSharks(g);
        }));
      }
    }
    // the pirate sea's kraken boss is built later in the load (match.ts
    // prebuildMapBeasts) — start its fetch now so it is parsed by then
    if (map.id === "pirate_ship") void preloadSeaModel(KRAKEN_URL);
    // NOTE: the sky GHOST SHIP is now a REAL, solid, landable flying galleon
    // driven by the map's `plane` config + the Plane class (server-authoritative
    // moving platform) — no longer a decorative ambient prop. spawnGhostShip()
    // stays defined but is intentionally not called (would double up the ship).

    this.attachHerds(); // realistic birds / hares / fish (ambientModels.ts)
    this.scene.add(this.grp);
  }

  private ghostShip: { g: THREE.Group; cx: number; cz: number; r: number; a: number; y: number } | null = null;

  private spawnGhostShip(): void {
    const e = this.env;
    const violet = new THREE.MeshStandardMaterial({ color: "#7a4bd0", emissive: "#4a2a9a", emissiveIntensity: 0.5, roughness: 0.5, transparent: true, opacity: 0.72 });
    const sail = new THREE.MeshStandardMaterial({ color: "#b79cf0", emissive: "#6a4ac0", emissiveIntensity: 0.4, roughness: 0.6, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
    const glow = new THREE.MeshBasicMaterial({ color: "#c8a6ff", transparent: true, opacity: 0.85 });
    const g = new THREE.Group();
    const box = (w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number): void => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat); m.position.set(x, y, z); g.add(m);
    };
    box(6, 3, 20, violet, 0, 0, 0);      // hull
    box(4, 2.4, 5, violet, 0, 0.3, 12);  // bow taper
    box(2, 1.8, 3, violet, 0, 0.6, 15);  // prow
    box(6.5, 3.2, 4, violet, 0, 1.6, -9); // stern castle
    box(6.2, 0.4, 20, violet, 0, 1.6, 0); // deck
    for (const mz of [-4, 4]) {
      box(0.5, 16, 0.5, violet, 0, 9, mz);      // mast
      box(9, 0.3, 0.3, violet, 0, 12, mz);      // yard
      box(7, 5, 0.2, sail, 0, 9.5, mz + 0.3);   // course sail (tattered look = flat panel)
      box(6, 4, 0.2, sail, 0, 15, mz + 0.3);    // topsail
    }
    box(0.9, 0.9, 0.9, glow, 0, 3.6, -10.6);    // spectral stern lantern
    g.scale.setScalar(2.2);
    this.grp.add(g);
    this.ghostShip = { g, cx: e.cx, cz: e.cz, r: Math.min(150, e.big * 0.32), a: 0, y: 58 };
  }

  private updateGhostShip(dt: number): void {
    const s = this.ghostShip;
    if (!s) return;
    s.a += dt * 0.055; // a slow, eerie circuit around the map
    const x = s.cx + Math.cos(s.a) * s.r;
    const z = s.cz + Math.sin(s.a) * s.r;
    s.g.position.set(x, s.y + Math.sin(s.a * 3) * 2.5, z); // bob on unseen swells
    s.g.rotation.y = -s.a + Math.PI / 2;                    // bow leads the turn
    s.g.rotation.z = Math.sin(s.a * 2) * 0.07;              // gentle roll
  }

  private swimmers: Swimmer[] = [];
  // Fish are drawn as TWO shared InstancedMeshes (bodies + tails), one draw call
  // each, instead of 2 Mesh per fish (up to 22 fish = 44 persistent draw calls on
  // a water map). `fishFree` is the free-slot pool; a killed fish returns its slot.
  private fishBodyIM: THREE.InstancedMesh | null = null;
  private fishTailIM: THREE.InstancedMesh | null = null;
  private fishFree: number[] = [];
  private water: { minX: number; maxX: number; minZ: number; maxZ: number; top: number; bottom: number } | null = null;
  // Birds: THREE shared InstancedMeshes (body cone + 2 wing boxes), 3 draw calls
  // in place of 3 Mesh per bird (5-8 birds = 15-24 persistent draw calls on EVERY
  // map). `birdFree` is the free-slot pool; a killed bird returns its slot.
  private birdBodyIM: THREE.InstancedMesh | null = null;
  private birdWingLIM: THREE.InstancedMesh | null = null;
  private birdWingRIM: THREE.InstancedMesh | null = null;
  private birdFree: number[] = [];
  // Critters: TWO shared InstancedMeshes (body-coloured torso/head/tail merge +
  // dark ears/legs merge), 2 draw calls in place of 2 Mesh per critter (4-7
  // critters = 8-14 persistent draw calls on EVERY map). `critterFree` is the
  // free-slot pool; a killed critter returns its slot.
  private critterBodyIM: THREE.InstancedMesh | null = null;
  private critterDarkIM: THREE.InstancedMesh | null = null;
  private critterFree: number[] = [];
  /** REALISTIC herds (ambientModels.ts): when set, the species draws through
   *  the baked rig and its procedural InstancedMeshes are hidden (kept — same
   *  slots, so spawn/kill bookkeeping is untouched). */
  private birdHerd: MorphHerd | null = null;
  private fishHerd: MorphHerd | null = null;
  private critterHerd: MorphHerd | null = null;

  /** Swap in the realistic herds: now if the GLB is parsed, else while the
   *  load screen is still up (renderer.precompile waits on the gate, so the
   *  swap is compiled with the scene). Late / failed / TV app: procedural. */
  private attachHerds(): void {
    const want = (url: string, apply: (g: GLTF) => void): void => {
      const g = ambientModelIfReady(url);
      if (g) { apply(g); return; }
      if (!realModelsEnabled()) return;
      const ticket = holdLoadScreen(preloadAmbientModel(url).then((m) => {
        if (m && !this.disposed && loadScreenOpen(ticket)) apply(m);
      }));
    };
    if (this.birdBodyIM) want(CROW_URL, (g) => {
      if (this.birdHerd || !this.birdBodyIM) return;
      const h = MorphHerd.from(g, this.birdBodyIM.count, CROW_LEN, CROW_TINTS);
      if (!h) return;
      this.birdHerd = h; this.grp.add(h.im);
      for (const im of [this.birdBodyIM, this.birdWingLIM, this.birdWingRIM]) if (im) im.visible = false;
    });
    if (this.critterBodyIM) want(HARE_URL, (g) => {
      if (this.critterHerd || !this.critterBodyIM) return;
      const h = MorphHerd.from(g, this.critterBodyIM.count, HARE_LEN, HARE_TINTS);
      if (!h) return;
      this.critterHerd = h; this.grp.add(h.im);
      for (const im of [this.critterBodyIM, this.critterDarkIM]) if (im) im.visible = false;
    });
    if (this.fishBodyIM) want(FISH_URL, (g) => {
      if (this.fishHerd || !this.fishBodyIM) return;
      const h = MorphHerd.from(g, this.fishBodyIM.count, FISH_LEN, FISH_TINTS);
      if (!h) return;
      this.fishHerd = h; this.grp.add(h.im);
      for (const im of [this.fishBodyIM, this.fishTailIM]) if (im) im.visible = false;
    });
  }

  /** Build the two shared fish InstancedMeshes (bodies + tails) and seed the
   *  free-slot pool. Geometry matches the old per-fish meshes exactly; the flatten
   *  scale / tail rotation-offset now ride the per-instance matrix instead of the
   *  mesh transform. Per-fish colour rides `instanceColor` (one shared material).
   *  SHADER WARM: instanced MeshToon + instanceColor is a distinct program, but
   *  the pool is built in the constructor which runs BEFORE renderer.precompile()
   *  in every entry path (match.ts) — so the real-scene compile warms it and it
   *  never cold-compiles mid-match (same reasoning as the decor/critter meshes). */
  private buildFishPool(cap: number): void {
    const mat = new THREE.MeshToonMaterial({ gradientMap: this.env.grad }); // white base × instanceColor = per-fish tint
    const bodyIM = new THREE.InstancedMesh(new THREE.SphereGeometry(0.22, 8, 6), mat, cap);
    const tailIM = new THREE.InstancedMesh(new THREE.ConeGeometry(0.18, 0.3, 4), mat, cap);
    for (const im of [bodyIM, tailIM]) {
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.frustumCulled = false; // one bounding sphere can't track a whole shoal; 2 draw calls over the water box is cheap
      for (let i = 0; i < cap; i++) im.setMatrixAt(i, FISH_ZERO_MAT); // every slot invisible until spawned
      im.setColorAt(0, FISH_TMP_COL.set("#ffffff")); // allocate the instanceColor buffer up-front
      im.instanceMatrix.needsUpdate = true;
      this.grp.add(im);
    }
    this.fishBodyIM = bodyIM; this.fishTailIM = tailIM;
    this.fishFree = [];
    for (let i = cap - 1; i >= 0; i--) this.fishFree.push(i);
  }

  /** Return a killed fish's instance slot to the pool and hide it. */
  private freeFishSlot(slot: number): void {
    this.fishBodyIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    this.fishTailIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    if (this.fishHerd) { this.fishHerd.hide(slot); this.fishHerd.flush(); }
    if (this.fishBodyIM) this.fishBodyIM.instanceMatrix.needsUpdate = true;
    if (this.fishTailIM) this.fishTailIM.instanceMatrix.needsUpdate = true;
    this.fishFree.push(slot);
  }

  /** Build the three shared bird InstancedMeshes (body cone + L/R wing boxes) and
   *  seed the free-slot pool. Geometry matches the old per-bird meshes exactly;
   *  the body's rotation.x=π/2 and each wing's ±0.36 offset + flap now ride the
   *  per-instance matrix (via the NON-rendered proxies) instead of a child mesh.
   *  Per-bird colour rides `instanceColor` (one shared material). SHADER WARM:
   *  same instanced MeshToon+instanceColor program the fish pool already warms,
   *  and the pool is built in the constructor which runs BEFORE precompile(). */
  private buildBirdPool(cap: number): void {
    const mat = new THREE.MeshToonMaterial({ gradientMap: this.env.grad }); // white base × instanceColor = per-bird tint
    const bodyIM = new THREE.InstancedMesh(new THREE.ConeGeometry(0.12, 0.6, 6), mat, cap);
    const wingLIM = new THREE.InstancedMesh(new THREE.BoxGeometry(0.7, 0.04, 0.28), mat, cap);
    const wingRIM = new THREE.InstancedMesh(new THREE.BoxGeometry(0.7, 0.04, 0.28), mat, cap);
    for (const im of [bodyIM, wingLIM, wingRIM]) {
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.frustumCulled = false; // birds are tiny (~20 tris each); 3 draw calls over the whole flock beats per-bird culling
      for (let i = 0; i < cap; i++) im.setMatrixAt(i, FISH_ZERO_MAT); // every slot invisible until spawned
      im.setColorAt(0, FISH_TMP_COL.set("#ffffff")); // allocate the instanceColor buffer up-front
      im.instanceMatrix.needsUpdate = true;
      this.grp.add(im);
    }
    this.birdBodyIM = bodyIM; this.birdWingLIM = wingLIM; this.birdWingRIM = wingRIM;
    this.birdFree = [];
    for (let i = cap - 1; i >= 0; i--) this.birdFree.push(i);
  }

  /** Return a killed bird's instance slot to the pool and hide it. */
  private freeBirdSlot(slot: number): void {
    this.birdBodyIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    this.birdWingLIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    this.birdWingRIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    if (this.birdHerd) { this.birdHerd.hide(slot); this.birdHerd.flush(); }
    if (this.birdBodyIM) this.birdBodyIM.instanceMatrix.needsUpdate = true;
    if (this.birdWingLIM) this.birdWingLIM.instanceMatrix.needsUpdate = true;
    if (this.birdWingRIM) this.birdWingRIM.instanceMatrix.needsUpdate = true;
    this.birdFree.push(slot);
  }

  /** Build the two shared critter InstancedMeshes (body-coloured merge + dark
   *  merge) and seed the free-slot pool. Geometry matches the old per-critter
   *  merged meshes exactly (offsets baked into the box geometry, then merged by
   *  material). Per-critter body tint (brown/grey) rides `instanceColor`; the
   *  dark ears/legs share one tint but still ride instanceColor so both IMs use
   *  the SAME instanced-MeshToon+instanceColor program the bird/fish pools
   *  already warm — no new shader variant cold-compiles mid-match. */
  private buildCritterPool(cap: number): void {
    const grad = this.env.grad;
    const at = (geo: THREE.BufferGeometry, x: number, y: number, z: number): THREE.BufferGeometry => geo.translate(x, y, z);
    let bodyGeo: THREE.BufferGeometry, darkGeo: THREE.BufferGeometry;
    if (richCreatures()) {
      // RICH tier (2026-10-23, the realistic-mobs pass): a hare-shaped rodent
      // of soft ellipsoids instead of nine boxes — a deep chest, the big
      // rounded haunch a hopping animal actually has, a tapered snout, a
      // cotton tail; ears, eyes, nose and the thin forelegs / long hind feet
      // in the dark merge. Same footprint, same two instanced draw calls, same
      // instanced-toon program; ~1k tris per critter (4-7 on a map).
      const S = 8; // ellipsoid segments: these are 0.6 m animals
      bodyGeo = bake([
        { g: blob(0.3, 0.26, 0.5, 1, S), p: [0, 0.3, 0.02] },                 // torso
        { g: blob(0.34, 0.3, 0.3, 1, S), p: [0, 0.27, 0.15] },                // haunch
        { g: blob(0.24, 0.25, 0.24, 1, S), p: [0, 0.34, -0.18] },             // chest
        { g: blob(0.21, 0.19, 0.25, 1, S), p: [0, 0.44, -0.33], r: [0.15, 0, 0] }, // head
        { g: blob(0.12, 0.1, 0.13, 1, S), p: [0, 0.41, -0.45] },              // snout
        { g: blob(0.1, 0.1, 0.1, 1, S), p: [0, 0.34, 0.31] },                 // cotton tail
      ]);
      darkGeo = bake([
        { g: blob(0.07, 0.2, 0.035, 1, S), p: [-0.055, 0.58, -0.3], r: [0.3, 0, 0.18] },  // earL
        { g: blob(0.07, 0.2, 0.035, 1, S), p: [0.055, 0.58, -0.3], r: [0.3, 0, -0.18] },  // earR
        { g: blob(0.045, 0.045, 0.045, 1, 6), p: [-0.085, 0.465, -0.4] },                  // eyes
        { g: blob(0.045, 0.045, 0.045, 1, 6), p: [0.085, 0.465, -0.4] },
        { g: blob(0.04, 0.03, 0.03, 1, 6), p: [0, 0.425, -0.515] },                         // nose
        { g: blob(0.055, 0.22, 0.06, 1, S), p: [-0.075, 0.11, -0.2] },                      // forelegs
        { g: blob(0.055, 0.22, 0.06, 1, S), p: [0.075, 0.11, -0.2] },
        { g: blob(0.075, 0.07, 0.24, 1, S), p: [-0.11, 0.035, 0.12] },                      // long hind feet
        { g: blob(0.075, 0.07, 0.24, 1, S), p: [0.11, 0.035, 0.12] },
      ]);
    } else {
      bodyGeo = mergeGeometries([
        at(new THREE.BoxGeometry(0.3, 0.26, 0.6), 0, 0.3, 0),         // torso
        at(new THREE.BoxGeometry(0.24, 0.24, 0.24), 0, 0.42, -0.34),  // head
        at(new THREE.BoxGeometry(0.07, 0.07, 0.2), 0, 0.32, 0.36),    // tail
      ])!;
      const darkGeos = [
        at(new THREE.BoxGeometry(0.06, 0.18, 0.05), -0.07, 0.58, -0.36), // earL
        at(new THREE.BoxGeometry(0.06, 0.18, 0.05), 0.07, 0.58, -0.36),  // earR
      ];
      for (const [lx, lz] of [[-0.1, -0.2], [0.1, -0.2], [-0.1, 0.2], [0.1, 0.2]] as [number, number][]) {
        darkGeos.push(at(new THREE.BoxGeometry(0.07, 0.22, 0.07), lx, 0.11, lz)); // legs
      }
      darkGeo = mergeGeometries(darkGeos)!;
    }
    const bodyMat = new THREE.MeshToonMaterial({ gradientMap: grad }); // white base × instanceColor = per-critter body tint
    const darkMat = new THREE.MeshToonMaterial({ gradientMap: grad }); // white base × instanceColor = dark ears/legs
    const bodyIM = new THREE.InstancedMesh(bodyGeo, bodyMat, cap);
    const darkIM = new THREE.InstancedMesh(darkGeo, darkMat, cap);
    for (const im of [bodyIM, darkIM]) {
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      im.frustumCulled = false; // critters wander the whole map; 2 draw calls over the flock beats per-critter culling (matches birds)
      // castShadow stays default-off: the old per-critter parts never cast a
      // shadow either (castShadow was set on the parent group, a no-op for
      // children in three), so the shadow pass is byte-identical.
      for (let i = 0; i < cap; i++) im.setMatrixAt(i, FISH_ZERO_MAT); // every slot invisible until spawned
      im.setColorAt(0, FISH_TMP_COL.set("#ffffff")); // allocate the instanceColor buffer up-front
      im.instanceMatrix.needsUpdate = true;
      this.grp.add(im);
    }
    this.critterBodyIM = bodyIM; this.critterDarkIM = darkIM;
    this.critterFree = [];
    for (let i = cap - 1; i >= 0; i--) this.critterFree.push(i);
  }

  /** Return a killed critter's instance slot to the pool and hide it. */
  private freeCritterSlot(slot: number): void {
    this.critterBodyIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    this.critterDarkIM?.setMatrixAt(slot, FISH_ZERO_MAT);
    if (this.critterHerd) { this.critterHerd.hide(slot); this.critterHerd.flush(); }
    if (this.critterBodyIM) this.critterBodyIM.instanceMatrix.needsUpdate = true;
    if (this.critterDarkIM) this.critterDarkIM.instanceMatrix.needsUpdate = true;
    this.critterFree.push(slot);
  }

  private spawnFish(): void {
    const wtr = this.water!;
    const slot = this.fishFree.pop();
    if (slot === undefined || !this.fishBodyIM || !this.fishTailIM) return; // pool exhausted / no water — nothing to do
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);
    const cols = ["#ff8c42", "#ffd166", "#4aa3df", "#e85d75", "#8ad4c1", "#c0c0d0"];
    // NON-rendered proxies: identical local transforms to the old fish meshes, so
    // updateSwimmers moves them exactly as before; the shared IMs draw the pixels.
    const g = new THREE.Object3D();
    const bodyProxy = new THREE.Object3D();
    bodyProxy.scale.set(1, 0.7, 1.8); // flattened, streamlined (was body.scale)
    const tail = new THREE.Object3D();
    tail.rotation.x = -Math.PI / 2; tail.position.z = 0.42; tail.scale.set(1, 0.5, 1);
    g.add(bodyProxy, tail);
    g.scale.setScalar(rand(0.7, 1.4));
    const col = FISH_TMP_COL.set(cols[(Math.random() * cols.length) | 0]);
    this.fishBodyIM.setColorAt(slot, col);
    this.fishTailIM.setColorAt(slot, col);
    if (this.fishBodyIM.instanceColor) this.fishBodyIM.instanceColor.needsUpdate = true;
    if (this.fishTailIM.instanceColor) this.fishTailIM.instanceColor.needsUpdate = true;
    this.swimmers.push({
      g, tail, bodyProxy, slot, x: rand(wtr.minX, wtr.maxX), y: rand(wtr.bottom, wtr.top), z: rand(wtr.minZ, wtr.maxZ),
      dir: rand(0, Math.PI * 2), pitch: 0, speed: rand(1.6, 3.2), t: rand(0, 10), turnAt: rand(1.5, 4), big: false,
    });
  }

  /** The parsed shark glTF this map's sharks use (null = procedural). Fixed
   *  for the round once the load screen closes, so respawns match. */
  private sharkModel: GLTF | null = null;
  private disposed = false;

  /** Load-screen swap: replace every procedural shark's visual with the real
   *  model in place (same group, so position/heading/scale carry over). */
  private upgradeSharks(g: GLTF): void {
    for (const s of this.swimmers) {
      if (!s.big || s.shark) continue;
      const rig = buildSharkModel(g);
      if (!rig) return; // asset unusable — keep the procedural sharks
      for (const c of [...s.g.children]) { s.g.remove(c); this.freeTree(c); }
      s.g.add(rig.root);
      s.tail = new THREE.Object3D(); // the swish target; the clip swims the real tail
      s.shark = rig;
    }
    this.sharkModel = g;
  }

  private spawnShark(): void {
    const wtr = this.water!;
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);
    const rig = this.sharkModel ? buildSharkModel(this.sharkModel) : null;
    if (rig) {
      // REALISTIC shark (seaModels.ts): one skinned draw call, scaled to the
      // procedural shark's length so the same group scale gives the same animal
      const g = new THREE.Group();
      g.add(rig.root);
      g.scale.setScalar(rand(1.5, 2.4));
      this.grp.add(g);
      this.swimmers.push({
        g, tail: new THREE.Object3D(), x: rand(wtr.minX, wtr.maxX), y: rand(wtr.bottom + 1, wtr.top - 1), z: rand(wtr.minZ, wtr.maxZ),
        dir: rand(0, Math.PI * 2), pitch: 0, speed: rand(2.2, 3.4), t: rand(0, 10), turnAt: rand(3, 6), big: true, shark: rig,
      });
      return;
    }
    const grey = new THREE.MeshToonMaterial({ color: "#6b7683", gradientMap: this.env.grad });
    const pale = new THREE.MeshToonMaterial({ color: "#c3ccd4", gradientMap: this.env.grad });
    const g = new THREE.Group();
    const tail = new THREE.Group();
    if (richCreatures()) {
      // RICH tier (2026-10-23, the realistic-mobs pass): a real shark
      // silhouette — fusiform body tapering to a pointed snout and a slim
      // caudal peduncle, a swept-back first dorsal, two angled pectorals, a
      // countershaded belly, black eyes, and the asymmetric (heterocercal)
      // tail with the long upper lobe. 4 draw calls, same as before; same
      // overall length/girth, so it occupies the same water.
      const fin = (r: number, h: number): THREE.BufferGeometry => new THREE.ConeGeometry(r, h, 4);
      const back = new THREE.Mesh(bake([
        { g: blob(1.2, 1.0, 3.3), p: [0, 0.1, -0.05] },                          // trunk
        { g: blob(0.9, 0.68, 1.5), p: [0, 0.07, -1.45], r: [0.06, 0, 0] },        // head
        { g: blob(0.38, 0.3, 0.55), p: [0, 0.02, -2.05] },                         // snout tip
        { g: blob(0.5, 0.58, 1.5), p: [0, 0.14, 1.4] },                            // caudal peduncle
        { g: fin(0.42, 0.95), p: [0, 0.8, -0.25], r: [0.5, 0, 0], s: [1, 1, 0.22] }, // 1st dorsal (swept)
        { g: fin(0.14, 0.3), p: [0, 0.5, 1.15], r: [0.5, 0, 0], s: [1, 1, 0.25] },  // 2nd dorsal
        { g: fin(0.33, 1.0), p: [-0.62, -0.18, -0.6], r: [-0.5, 0, 1.9], s: [1, 1, 0.2] }, // pectorals
        { g: fin(0.33, 1.0), p: [0.62, -0.18, -0.6], r: [-0.5, 0, -1.9], s: [1, 1, 0.2] },
      ]), grey);
      const belly = new THREE.Mesh(bake([
        { g: blob(1.0, 0.5, 3.0), p: [0, -0.2, -0.1] },
        { g: blob(0.74, 0.38, 1.3), p: [0, -0.12, -1.4] },
      ]), pale);
      const eyes = new THREE.Mesh(bake([
        { g: blob(0.1, 0.1, 0.1, 1, 6), p: [-0.38, 0.18, -1.72] },
        { g: blob(0.1, 0.1, 0.1, 1, 6), p: [0.38, 0.18, -1.72] },
      ]), new THREE.MeshToonMaterial({ color: "#14171c", gradientMap: this.env.grad }));
      const caudal = new THREE.Mesh(bake([
        { g: fin(0.36, 1.35), p: [0, 0.5, 0.4], r: [0.75, 0, 0], s: [1, 1, 0.2] },               // upper lobe
        { g: fin(0.28, 0.8), p: [0, -0.28, 0.32], r: [Math.PI - 0.75, 0, 0], s: [1, 1, 0.2] },  // lower lobe
      ]), grey);
      tail.add(caudal); tail.position.z = 1.9;
      g.add(back, belly, eyes, tail);
    } else {
      const body = new THREE.Mesh(new THREE.SphereGeometry(0.6, 10, 8), grey);
      body.scale.set(1, 0.85, 3.4); body.position.y = 0.1;
      const belly = new THREE.Mesh(new THREE.SphereGeometry(0.5, 8, 6), pale);
      belly.scale.set(0.9, 0.5, 3.0); belly.position.y = -0.2;
      const dorsal = new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.8, 4), grey);
      dorsal.position.set(0, 0.7, -0.2); dorsal.rotation.x = -0.2;
      const fin = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.1, 4), grey);
      fin.rotation.x = -Math.PI / 2; fin.position.z = 0.4; fin.scale.set(0.4, 1, 1);
      tail.add(fin); tail.position.z = 1.9;
      g.add(body, belly, dorsal, tail);
    }
    g.scale.setScalar(rand(1.5, 2.4));
    this.grp.add(g);
    this.swimmers.push({
      g, tail, x: rand(wtr.minX, wtr.maxX), y: rand(wtr.bottom + 1, wtr.top - 1), z: rand(wtr.minZ, wtr.maxZ),
      dir: rand(0, Math.PI * 2), pitch: 0, speed: rand(2.2, 3.4), t: rand(0, 10), turnAt: rand(3, 6), big: true,
    });
  }

  private updateSwimmers(dt: number): void {
    const wtr = this.water;
    if (!wtr) return;
    let fishMoved = false;
    for (const s of this.swimmers) {
      s.t += dt;
      // gentle wander: re-pick a heading periodically
      if (s.t > s.turnAt) { s.t = 0; s.turnAt = 1.5 + Math.random() * 4; s.dir += (Math.random() - 0.5) * 1.3; s.pitch = (Math.random() - 0.5) * 0.5; }
      // steer away from the water box edges + depth limits
      if (s.x < wtr.minX + 6) s.dir = 0; else if (s.x > wtr.maxX - 6) s.dir = Math.PI;
      if (s.z < wtr.minZ + 6) s.dir = Math.PI / 2; else if (s.z > wtr.maxZ - 6) s.dir = -Math.PI / 2;
      if (s.y > wtr.top - 0.6) s.pitch = -Math.abs(s.pitch) - 0.2; else if (s.y < wtr.bottom + 0.6) s.pitch = Math.abs(s.pitch) + 0.2;
      // heading unit vector as scalars — NOT a new THREE.Vector3 per swimmer per
      // frame (up to 25 allocs/frame on a water map — an invariant-6 leak). Same
      // float ops, and cos(pitch) is now computed once instead of twice, so the
      // result is bit-identical to the old Vector3 path.
      const cp = Math.cos(s.pitch), sp = Math.sin(s.pitch);
      const fwdX = -Math.sin(s.dir) * cp, fwdY = sp, fwdZ = -Math.cos(s.dir) * cp;
      s.x += fwdX * s.speed * dt; s.y += fwdY * s.speed * dt; s.z += fwdZ * s.speed * dt;
      s.g.position.set(s.x, s.y, s.z);
      s.g.rotation.set(0, s.dir, 0);
      s.g.rotateX(s.pitch);
      // tail swish (faster for the little fish)
      s.tail.rotation.y = Math.sin(performance.now() * (s.big ? 0.006 : 0.012) + s.x) * (s.big ? 0.4 : 0.6);
      if (s.shark) updateSharkModel(s.shark, dt, s.speed); // the real shark swims its own clip
      // fish: push the just-computed body/tail world transforms into the shared
      // InstancedMeshes (sharks are `big` and stay a real rendered Group). The
      // proxies are outside the scene graph, so update their matrices by hand.
      if (!s.big && s.slot !== undefined && s.bodyProxy && this.fishBodyIM && this.fishTailIM) {
        s.g.updateMatrixWorld(true);
        this.fishBodyIM.setMatrixAt(s.slot, s.bodyProxy.matrixWorld);
        this.fishTailIM.setMatrixAt(s.slot, s.tail.matrixWorld);
        if (this.fishHerd) {
          // realistic fish: the body transform (heading + pitch + size), its
          // own tail-beat phase — quicker when it swims faster
          s.ph = (s.ph ?? Math.random()) + dt / FISH_BEAT * (s.speed / 2.4);
          this.fishHerd.setMatrix(s.slot, s.g.matrixWorld);
          this.fishHerd.setPose(s.slot, s.ph);
        }
        fishMoved = true;
      }
    }
    if (fishMoved) { this.fishBodyIM!.instanceMatrix.needsUpdate = true; this.fishTailIM!.instanceMatrix.needsUpdate = true; this.fishHerd?.flush(); }
  }

  private dragons: Dragon[] = [];

  /** Mount the realistic dragon on every soaring dragon NOW if the module is
   *  armed (match.ts, under the load screen) instead of lazily in the first
   *  live update(). Returns how many were mounted. */
  mountRealModels(): number {
    if (!dragonModelArmed()) return 0;
    let n = 0;
    for (const dr of this.dragons) if (!dr.model) { dr.model = mountDragonModel(dr.g, dr.tint); n++; }
    return n;
  }

  /** Distance from (x,y,z) to the nearest AMBIENT dragon, Infinity when this
   *  map has none. Feeds the dragon-proximity music: flying near one of the
   *  soaring castle dragons now swells the medieval theme exactly like the
   *  rideable one does ("all dragons do the music thing" — owner 2026-08-08). */
  nearestDragonDist(x: number, y: number, z: number): number {
    let best = Infinity;
    for (const dr of this.dragons) {
      const p = dr.g.position;
      const d2 = Math.hypot(p.x - x, p.y - y, p.z - z);
      if (d2 < best) best = d2;
    }
    return best;
  }

  /** A ~30m-wingspan european dragon soaring above the battlements. */
  private spawnDragon(i: number): void {
    const e = this.env;
    const palettes = [
      { body: "#3f6b30", membrane: "#26421f", belly: "#9aa06a" }, // forest green
      { body: "#7a2833", membrane: "#4d1720", belly: "#b08a72" }, // crimson
      { body: "#34363f", membrane: "#1e2027", belly: "#6a6f78" }, // black
    ];
    const rig = buildEuropeanDragon(e.grad, palettes[i % 3]);
    rig.group.scale.setScalar(3.0 + i * 0.5);
    this.grp.add(rig.group);
    this.dragons.push({
      g: rig.group, wingL: rig.wingL, wingR: rig.wingR,
      cx: e.cx, cz: e.cz,
      r: [70, 95, 118][i % 3],
      a: Math.random() * Math.PI * 2,
      speed: [0.14, -0.1, 0.08][i % 3], // one flies counter-circuit
      y: [46, 60, 72][i % 3],
      ph: Math.random() * 10,
      tint: palettes[i % 3].body,
      model: null,
    });
  }

  private spawnBird(seed = 0): void {
    const e = this.env;
    const slot = this.birdFree.pop();
    if (slot === undefined || !this.birdBodyIM || !this.birdWingLIM || !this.birdWingRIM) return; // pool exhausted — nothing to do
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);
    const birdColors = ["#20242e", "#3a3f4d", "#5a4632", "#2a2f3d"];
    // NON-rendered proxies: identical local transforms to the old bird meshes, so
    // updateBirds moves them exactly as before; the shared IMs draw the pixels.
    const g = new THREE.Object3D();
    const body = new THREE.Object3D();
    body.rotation.x = Math.PI / 2; // point forward (-z)
    const wingL = new THREE.Object3D(); wingL.position.set(-0.36, 0, 0);
    const wingR = new THREE.Object3D(); wingR.position.set(0.36, 0, 0);
    g.add(body, wingL, wingR);
    g.scale.setScalar(rand(0.7, 1.3));
    const col = FISH_TMP_COL.set(birdColors[seed % birdColors.length]);
    this.birdBodyIM.setColorAt(slot, col);
    this.birdWingLIM.setColorAt(slot, col);
    this.birdWingRIM.setColorAt(slot, col);
    if (this.birdBodyIM.instanceColor) this.birdBodyIM.instanceColor.needsUpdate = true;
    if (this.birdWingLIM.instanceColor) this.birdWingLIM.instanceColor.needsUpdate = true;
    if (this.birdWingRIM.instanceColor) this.birdWingRIM.instanceColor.needsUpdate = true;
    this.birds.push({ g, body, wingL, wingR, slot, cx: e.cx + rand(-e.spanX * 0.2, e.spanX * 0.2), cz: e.cz + rand(-e.spanZ * 0.2, e.spanZ * 0.2), r: rand(e.big * 0.15, e.big * 0.35), a: rand(0, Math.PI * 2), speed: rand(0.2, 0.45), y: rand(e.maxY * 0.5 + 8, e.maxY * 0.5 + 18) });
  }

  private spawnCritter(): void {
    const e = this.env;
    const slot = this.critterFree.pop();
    if (slot === undefined || !this.critterBodyIM || !this.critterDarkIM) return; // pool exhausted — nothing to do
    const rand = (a: number, b: number): number => a + Math.random() * (b - a);
    // Per-critter body tint (brown or grey) rides instanceColor; the dark
    // ears/legs share one tint. The geometry lives in the shared pool IMs — the
    // proxy `g` is NON-rendered and only carries the whole-body transform, which
    // updateCritters writes into both instance slots each frame.
    this.critterBodyIM.setColorAt(slot, FISH_TMP_COL.set(rand(0, 1) > 0.5 ? "#8a6a4a" : "#5a5f6a"));
    this.critterDarkIM.setColorAt(slot, FISH_TMP_COL.set("#2a2f3d"));
    if (this.critterBodyIM.instanceColor) this.critterBodyIM.instanceColor.needsUpdate = true;
    if (this.critterDarkIM.instanceColor) this.critterDarkIM.instanceColor.needsUpdate = true;
    const g = new THREE.Object3D();
    const mx = e.minX + e.spanX * 0.12, Mx = e.maxX - e.spanX * 0.12;
    const mz = e.minZ + e.spanZ * 0.12, Mz = e.maxZ - e.spanZ * 0.12;
    // spawn only on solid ground (maps with gaps/water have voids in bounds)
    let x = 0, z = 0, gy: number | null = null;
    for (let tries = 0; tries < 24 && gy === null; tries++) {
      x = rand(mx, Mx); z = rand(mz, Mz);
      gy = this.groundAt(x, z);
    }
    if (gy === null) { this.critterFree.push(slot); return; } // no ground found — hand the slot back
    g.position.set(x, gy, z);
    this.critters.push({ g, slot, x, z, gy, dir: rand(0, Math.PI * 2), speed: rand(1.1, 2.2), t: rand(0, 10), turnAt: rand(1.5, 4), minX: mx, maxX: Mx, minZ: mz, maxZ: Mz });
  }

  /** A killed animal returns after 8-18s, somewhere else on the map. */
  private scheduleRespawn(kind: "bird" | "critter" | "fish" | "shark"): void {
    this.respawnQueue.push({ kind, at: performance.now() + 8000 + Math.random() * 10000 });
  }

  /** GORE easter egg: the animal detonates into a burst of giblets. */
  private goreBurst(pos: THREE.Vector3, big: boolean): void {
    const colors = ["#8a1420", "#a51d2d", "#5e0e16", "#c22f3d", "#3d0a10"];
    const n = big ? 14 : 10;
    const now = performance.now();
    for (let i = 0; i < n; i++) {
      const sz = 0.05 + Math.random() * 0.1;
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(sz, sz * (0.6 + Math.random() * 0.9), sz),
        new THREE.MeshBasicMaterial({ color: colors[i % colors.length] }),
      );
      m.position.copy(pos);
      const a = Math.random() * Math.PI * 2;
      const sp = 2.5 + Math.random() * 5.5;
      this.grp.add(m);
      this.gore.push({
        m,
        vx: Math.cos(a) * sp, vy: 2.5 + Math.random() * 5, vz: Math.sin(a) * sp,
        sx: (Math.random() - 0.5) * 14, sz: (Math.random() - 0.5) * 14,
        born: now,
      });
    }
  }

  /** Remove + deep-free a killed animal (respawn builds a brand-new one).
   *  Without this every wildlife kill leaked its geometries/materials. */
  private reap(g: THREE.Object3D): void {
    this.grp.remove(g);
    this.freeTree(g);
  }

  /** Deep-free a subtree's own buffers/materials. Anything tagged
   *  userData.shared (the cached glTF shark's geometry/material/textures) is
   *  left alone; a skinned clone's per-instance Skeleton is freed. */
  private freeTree(g: THREE.Object3D): void {
    disposeSkinned(g);
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!(m.geometry as { userData?: { shared?: boolean } })?.userData?.shared) m.geometry?.dispose?.();
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mat of mats) {
        if (mat.userData?.shared) continue;
        const t = mat as THREE.MeshStandardMaterial;
        if (t.map && !t.map.userData?.shared) t.map.dispose();
        mat.dispose();
      }
    });
  }

  update(dt: number): void {
    const d = Math.min(0.05, dt);
    this.updateSwimmers(d); // fish + sharks in the water volume
    this.updateGhostShip(d); // the violet sky galleon
    // giblets: ballistic tumble, gone after ~1.1s
    const nowMs = performance.now();
    // bring killed animals back (fresh random spot, staggered)
    for (let i = this.respawnQueue.length - 1; i >= 0; i--) {
      if (nowMs >= this.respawnQueue[i].at) {
        const { kind } = this.respawnQueue[i];
        this.respawnQueue.splice(i, 1);
        if (kind === "bird") this.spawnBird(Math.floor(Math.random() * 4));
        else if (kind === "fish") this.spawnFish();
        else if (kind === "shark") this.spawnShark();
        else this.spawnCritter();
      }
    }
    for (let i = this.gore.length - 1; i >= 0; i--) {
      const g = this.gore[i];
      g.vy -= 20 * d;
      g.m.position.x += g.vx * d; g.m.position.y += g.vy * d; g.m.position.z += g.vz * d;
      g.m.rotation.x += g.sx * d; g.m.rotation.z += g.sz * d;
      const age = nowMs - g.born;
      if (age > 700) g.m.scale.multiplyScalar(1 - d * 3);
      if (age > 1100 || g.m.position.y < -10) {
        this.grp.remove(g.m);
        g.m.geometry.dispose(); (g.m.material as THREE.Material).dispose();
        this.gore.splice(i, 1);
      }
    }
    // dragons: slow soaring circles with a bank into the turn. Like the birds
    // below, they now FLAP IN BURSTS and glide between them (wings held in a
    // shallow dihedral, riding thermals) — a big soarer beating constantly was
    // the least dragon-like thing about them. The glide phase also dips the
    // whole body slightly and the flap burst climbs it back, so altitude
    // visibly follows the wingbeats.
    for (const dr of this.dragons) {
      dr.a += dr.speed * d;
      const x = dr.cx + Math.cos(dr.a) * dr.r;
      const z = dr.cz + Math.sin(dr.a) * dr.r;
      const cyc = Math.sin(nowMs / 4200 + dr.ph * 1.7); // ~13s flap/glide cycle
      const flapping = cyc > 0.15;
      const beat = Math.sin(nowMs / 300 + dr.ph);
      const flap = flapping
        ? beat * 0.46 + 0.08                            // powered burst
        : 0.2 + Math.sin(nowMs / 1100 + dr.ph) * 0.05;  // gliding dihedral, tiny sway
      const lift = flapping ? 1.4 : -1.2;               // climb on the burst, sink on the glide
      dr.g.position.set(x, dr.y + Math.sin(dr.a * 2.2 + dr.ph) * 2.4 + lift * Math.abs(cyc) * 0.8, z);
      dr.g.rotation.y = -dr.a + (dr.speed > 0 ? Math.PI / 2 : -Math.PI / 2);
      dr.g.rotation.z = (dr.speed > 0 ? 1 : -1) * 0.16; // lean into the circle
      dr.g.rotation.x = flapping ? -0.04 : 0.06;        // nose up climbing, down gliding
      dr.wingR.rotation.z = flap;
      dr.wingL.rotation.z = flap;
      dr.wingR.rotation.y = flapping ? beat * 0.1 : 0.02;
      dr.wingL.rotation.y = flapping ? beat * 0.1 : 0.02;
      // REALISTIC DRAGON: armed only under the load screen (warmDragonModel),
      // so this swap lands before the first visible frame, never mid-fight.
      if (!dr.model && dragonModelArmed()) dr.model = mountDragonModel(dr.g, dr.tint);
      // same flap/glide bursts, same beat (1000/300 rad/s) as the wings above
      if (dr.model) dr.model.update(d, flapping ? 1 : 0, 1000 / 300);
    }
    let birdMoved = false;
    for (let i = this.birds.length - 1; i >= 0; i--) {
      const b = this.birds[i];
      if (b.dead) { // shot down: tumble + fall
        b.vy = (b.vy ?? 0) - 22 * d;
        b.g.position.y += b.vy * d;
        b.g.rotation.z += 8 * d; b.g.rotation.x += 5 * d;
        if (b.g.position.y < 0) { this.freeBirdSlot(b.slot); this.birds.splice(i, 1); this.scheduleRespawn("bird"); continue; }
      } else {
        b.a += b.speed * d;
        const x = b.cx + Math.cos(b.a) * b.r;
        const z = b.cz + Math.sin(b.a) * b.r;
        b.g.position.set(x, b.y + Math.sin(b.a * 3) * 0.6, z);
        // face along the tangent of the circle
        b.g.rotation.y = -b.a + Math.PI / 2;
        // flap in bursts, then glide (wings held slightly up) — reads far more
        // bird-like than constant flapping, costs nothing
        const gliding = Math.sin(b.a * 0.9 + b.cz) > 0.35;
        const flap = gliding ? 0.18 : Math.sin(performance.now() / 90 + b.cx) * 0.6;
        b.wingL.rotation.z = flap; b.wingR.rotation.z = -flap;
        b.g.rotation.z = Math.sin(b.a) * 0.12; // gentle bank into the turn
      }
      // push the just-computed body/wing world transforms into the shared IMs
      // (the proxies live outside the scene graph, so update their world matrices
      // by hand). Same treatment as the fish pool.
      b.g.updateMatrixWorld(true);
      this.birdBodyIM!.setMatrixAt(b.slot, b.body.matrixWorld);
      this.birdWingLIM!.setMatrixAt(b.slot, b.wingL.matrixWorld);
      this.birdWingRIM!.setMatrixAt(b.slot, b.wingR.matrixWorld);
      if (this.birdHerd) {
        if (b.dead) {
          this.birdHerd.setMatrix(b.slot, b.g.matrixWorld); // tumbling: any heading reads right
        } else {
          // the realistic bird flies ALONG its circle (beak on the tangent —
          // heading π−a), banked into the turn with the procedural wobble
          HERD_TMP.position.copy(b.g.position);
          HERD_TMP.rotation.set(0, Math.PI - b.a, -0.16 + Math.sin(b.a) * 0.1, "YXZ");
          HERD_TMP.scale.copy(b.g.scale);
          HERD_TMP.updateMatrix();
          this.birdHerd.setMatrix(b.slot, HERD_TMP.matrix);
        }
        // the same flap/glide bursts as the procedural wings, eased so the
        // pose never snaps between the beat and the held glide
        const gliding = !b.dead && Math.sin(b.a * 0.9 + b.cz) > 0.35;
        b.gw = (b.gw ?? 0) + ((gliding ? 1 : 0) - (b.gw ?? 0)) * (1 - Math.exp(-d * 4));
        b.ph = (b.ph ?? Math.random()) + d / CROW_BEAT * (b.dead ? 2.5 : 1);
        this.birdHerd.setPose(b.slot, b.ph, b.gw);
      }
      birdMoved = true;
    }
    if (birdMoved && this.birdBodyIM) {
      this.birdBodyIM.instanceMatrix.needsUpdate = true;
      this.birdWingLIM!.instanceMatrix.needsUpdate = true;
      this.birdWingRIM!.instanceMatrix.needsUpdate = true;
      this.birdHerd?.flush();
    }
    let critterMoved = false;
    for (let i = this.critters.length - 1; i >= 0; i--) {
      const c = this.critters[i];
      if (c.dead) { // shot: flop over + fade away (unreachable today — kills free the slot immediately — but kept correct)
        c.g.rotation.z = Math.min(Math.PI / 2, c.g.rotation.z + 6 * d);
        c.g.scale.multiplyScalar(1 - d * 1.5);
        if (performance.now() - c.dead > 900) { this.freeCritterSlot(c.slot); this.critters.splice(i, 1); this.scheduleRespawn("critter"); continue; }
      } else {
        c.t += d;
        if (c.t > c.turnAt) { c.t = 0; c.turnAt = 1.5 + Math.random() * 4; c.dir += (Math.random() - 0.5) * 2.2; }
        let nx = c.x + -Math.sin(c.dir) * c.speed * d;
        let nz = c.z + -Math.cos(c.dir) * c.speed * d;
        // bounce off the wander bounds — reflect the matching velocity component
        // (vx = -sin(dir) flips under dir→-dir; vz = -cos(dir) under dir→π-dir).
        // The old code used π-dir for BOTH, so an x-wall never repelled and the
        // critter sat there flip-flopping direction every frame (the "shakes").
        if (nx < c.minX || nx > c.maxX) { c.dir = -c.dir; nx = c.x; }
        if (nz < c.minZ || nz > c.maxZ) { c.dir = Math.PI - c.dir; nz = c.z; }
        // stay on solid ground: turn away from voids (map gaps) and cliffs
        const gy = this.groundAt(nx, nz);
        if (gy === null || Math.abs(gy - c.gy) > 1.1) {
          c.dir += Math.PI * (0.75 + Math.random() * 0.5);
          c.t = 0;
        } else {
          c.x = nx; c.z = nz; c.gy = gy;
        }
        // little hop — the realistic hare's hop is in its baked cycle instead
        c.g.position.set(c.x, this.critterHerd ? c.gy : c.gy + Math.abs(Math.sin(performance.now() / 150 + c.x)) * 0.14, c.z);
        c.ph = (c.ph ?? Math.random()) + d * c.speed / HARE_STRIDE;
        c.g.rotation.y = c.dir;
      }
      // push the whole-body transform into both shared InstancedMeshes. The
      // proxy is outside the scene graph, so update its matrix by hand (the two
      // merged parts share the group's world matrix — they had no local offset
      // beyond what is already baked into the pool geometry).
      if (this.critterBodyIM && this.critterDarkIM) {
        c.g.updateMatrixWorld(true);
        this.critterBodyIM.setMatrixAt(c.slot, c.g.matrixWorld);
        this.critterDarkIM.setMatrixAt(c.slot, c.g.matrixWorld);
        if (this.critterHerd) { this.critterHerd.setMatrix(c.slot, c.g.matrixWorld); this.critterHerd.setPose(c.slot, c.ph ?? 0); }
        critterMoved = true;
      }
    }
    if (critterMoved && this.critterBodyIM) {
      this.critterBodyIM.instanceMatrix.needsUpdate = true;
      this.critterDarkIM!.instanceMatrix.needsUpdate = true;
      this.critterHerd?.flush();
    }
  }

  /** The local player fired — kill the nearest live animal along the ray
   *  (client-only, purely for fun). EASTER EGG: it detonates in a gore burst.
   *  Returns the kill position (for SFX + the server bonus roll) or null. */
  tryShoot(origin: THREE.Vector3, dir: THREE.Vector3, fat = 1): { at: THREE.Vector3; kind: "bird" | "critter" | "fish" | "shark" } | null {
    const oc = new THREE.Vector3();
    const near = (pos: THREE.Vector3, radiusBase: number): number => {
      const radius = radiusBase * fat;
      oc.subVectors(pos, origin);
      const t = oc.dot(dir);
      if (t < 0 || t > 200) return Infinity;
      const perp = Math.sqrt(Math.max(0, oc.lengthSq() - t * t));
      return perp < radius ? t : Infinity;
    };
    let bestT = Infinity; let kill: (() => { at: THREE.Vector3; kind: "bird" | "critter" | "fish" | "shark" }) | null = null;
    for (let i = this.birds.length - 1; i >= 0; i--) {
      const b = this.birds[i];
      if (b.dead) continue;
      const t = near(b.g.position, 1.0);
      if (t < bestT) {
        bestT = t;
        kill = () => {
          const at = b.g.position.clone();
          this.goreBurst(at, true);
          this.freeBirdSlot(b.slot);
          this.birds.splice(i, 1);
          this.scheduleRespawn("bird");
          return { at, kind: "bird" as const };
        };
      }
    }
    for (let i = this.critters.length - 1; i >= 0; i--) {
      const c = this.critters[i];
      if (c.dead) continue;
      const at0 = new THREE.Vector3(c.g.position.x, c.g.position.y + 0.35, c.g.position.z);
      const t = near(at0, 0.85);
      if (t < bestT) {
        bestT = t;
        kill = () => {
          this.goreBurst(at0, false);
          this.freeCritterSlot(c.slot);
          this.critters.splice(i, 1);
          this.scheduleRespawn("critter");
          return { at: at0, kind: "critter" as const };
        };
      }
    }
    // SEA CREATURES: fish + sharks are shootable too — dive down and hunt them.
    for (let i = this.swimmers.length - 1; i >= 0; i--) {
      const s = this.swimmers[i];
      const t = near(s.g.position, s.big ? 1.4 : 0.7);
      if (t < bestT) {
        bestT = t;
        kill = () => {
          const at = s.g.position.clone();
          this.goreBurst(at, s.big);
          s.shark?.mixer.stopAllAction();
          this.reap(s.g);
          if (!s.big && s.slot !== undefined) this.freeFishSlot(s.slot);
          this.swimmers.splice(i, 1);
          const kind = s.big ? ("shark" as const) : ("fish" as const);
          this.scheduleRespawn(kind);
          return { at, kind };
        };
      }
    }
    return kill ? kill() : null;
  }

  /** An EXPLOSION near the wildlife: gib every animal within the radius
   *  (rockets, grenades, vehicle blasts). Returns how many died. */
  tryBlast(x: number, y: number, z: number, radius: number): number {
    let killed = 0;
    for (let i = this.birds.length - 1; i >= 0; i--) {
      const b = this.birds[i];
      if (b.dead) continue;
      if (b.g.position.distanceTo(new THREE.Vector3(x, y, z)) < radius + 1.2) {
        this.goreBurst(b.g.position.clone(), true);
        this.freeBirdSlot(b.slot); this.birds.splice(i, 1); this.scheduleRespawn("bird");
        killed++;
      }
    }
    for (let i = this.critters.length - 1; i >= 0; i--) {
      const c = this.critters[i];
      if (c.dead) continue;
      if (Math.hypot(c.g.position.x - x, c.g.position.y + 0.35 - y, c.g.position.z - z) < radius + 0.8) {
        this.goreBurst(new THREE.Vector3(c.g.position.x, c.g.position.y + 0.35, c.g.position.z), false);
        this.freeCritterSlot(c.slot); this.critters.splice(i, 1); this.scheduleRespawn("critter");
        killed++;
      }
    }
    // sea creatures caught in a blast (torpedoes, grenades sinking into the sea)
    for (let i = this.swimmers.length - 1; i >= 0; i--) {
      const s = this.swimmers[i];
      if (s.g.position.distanceTo(new THREE.Vector3(x, y, z)) < radius + (s.big ? 1.6 : 0.9)) {
        this.goreBurst(s.g.position.clone(), s.big);
        s.shark?.mixer.stopAllAction();
        this.reap(s.g);
        if (!s.big && s.slot !== undefined) this.freeFishSlot(s.slot);
        this.swimmers.splice(i, 1); this.scheduleRespawn(s.big ? "shark" : "fish");
        killed++;
      }
    }
    return killed;
  }

  dispose(): void {
    this.disposed = true;
    this.scene.remove(this.grp);
    // realistic dragons: mixer + per-instance skeleton/hide (the GLB is shared)
    for (const dr of this.dragons) { dr.model?.dispose(); dr.model = null; }
    for (const s of this.swimmers) s.shark?.mixer.stopAllAction();
    disposeSkinned(this.grp); // glTF sharks: their per-instance bone textures
    // InstancedMesh extends Mesh, so the fish + bird IMs' geometry + shared
    // material are freed by this traverse alongside every other wildlife mesh.
    // The cached glTF shark's geometry/material are userData.shared — kept.
    this.grp.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      if (!o.geometry.userData.shared) o.geometry.dispose();
      const mat = o.material as THREE.Material;
      if (!mat.userData?.shared) mat.dispose?.();
    });
    // ...but geometry.dispose() does NOT free an InstancedMesh's per-instance
    // buffers (instanceMatrix/instanceColor) — those are freed only when
    // IM.dispose() dispatches its 'dispose' event (see worldEntities blobIM).
    // Without this they leak on the shared renderer scene every match teardown.
    for (const im of [this.fishBodyIM, this.fishTailIM, this.birdBodyIM, this.birdWingLIM, this.birdWingRIM, this.critterBodyIM, this.critterDarkIM, this.birdHerd?.im, this.fishHerd?.im, this.critterHerd?.im]) im?.dispose();
    this.birdHerd = null; this.fishHerd = null; this.critterHerd = null;
    this.birds = []; this.critters = []; this.swimmers = [];
    this.fishBodyIM = null; this.fishTailIM = null; this.fishFree = [];
    this.birdBodyIM = null; this.birdWingLIM = null; this.birdWingRIM = null; this.birdFree = [];
    this.critterBodyIM = null; this.critterDarkIM = null; this.critterFree = [];
  }
}
